/**
 * Canonical academic domain constants — class/grade, target exam, subject.
 *
 * These apply at the domain-core level (students, teachers, modules,
 * questions) rather than being tied to any one feature. Validated at the API
 * boundary and persisted in CosmosDB.
 *
 * Single source of truth for both backend (Azure Functions) and web apps
 * (student, teacher, admin). Web apps consume via `@padcanvas/shared`; the
 * shared-ui package re-uses these via peer-dep resolution through the
 * consuming app's node_modules.
 */

export const GRADE_OPTIONS = ['6', '7', '8', '9', '10', '11', '12'] as const;
export type GradeType = (typeof GRADE_OPTIONS)[number];

// ── Programmes: base · extended · full ──────────────────────────────────────
// The PROGRAMME a curriculum belongs to — who defines the syllabus. Named
// TARGET_EXAM for history: it has always held both school boards (CBSE, ICSE)
// and exam tracks (JEE, NEET). Same three-tier shape as the subject lists below.
// See docs/FeatureDesigns/modules-curriculum-topics-concepts/curriculum-identity-and-eligibility-target.md §1.

// BASE — what every student and teacher sees. A surface offers these unless it
// deliberately asks for more, so nothing new can leak into a picker by default.
// Order defines priority (first = highest).
export const PROGRAMME_OPTIONS = ['JEE', 'NEET', 'ICSE', 'CBSE', 'STATE'] as const;
export type BaseProgrammeType = (typeof PROGRAMME_OPTIONS)[number];

// INTERNATIONAL — awarding bodies a student genuinely registers into, like the
// base five, but offered only behind the `international_programmes` gate: no
// question carries their pool keys yet, so an ungated student would register
// into empty practice.
//
// The programme is the BODY that sets the syllabus, never one of its
// qualifications: IB, not "MYP"; Cambridge, not "IGCSE" or "A Level". A student
// moving from class 10 to 11 moves from IGCSE to AS Level without changing
// programme, the same way ICSE covers the ISC classes. The qualification is a
// label derived from (programme, grade) — see PROGRAMME_STAGES.
// See docs/FeatureDesigns/international-programmes-target.md.
export const INTERNATIONAL_PROGRAMME_OPTIONS = ['IB', 'CAMBRIDGE'] as const;
export type InternationalProgrammeType = (typeof INTERNATIONAL_PROGRAMME_OPTIONS)[number];

// EXAM VARIANTS — the exams within a programme that set different papers. The
// programme stays the unit everything else keys on (a student's target exam,
// curricula, goals, pool keys): a JEE student prepares for JEE, while one PAPER
// is JEE Main or JEE Advanced, and those two are printed, timed and marked
// differently. So a paper names its variant beside its programme, never instead
// of it. A programme absent here has one kind of paper.
export const EXAM_VARIANTS = {
    JEE: ['jee-main', 'jee-advanced'],
} as const satisfies Partial<Record<BaseProgrammeType, readonly string[]>>;
export type ExamVariant = (typeof EXAM_VARIANTS)[keyof typeof EXAM_VARIANTS][number];

export const EXAM_VARIANT_LABELS: Record<ExamVariant, string> = {
    'jee-main': 'JEE Main',
    'jee-advanced': 'JEE Advanced',
};

/** The variants a programme's papers come in; empty for a programme with one kind. */
export function examVariantsOf(programme: string | null | undefined): readonly ExamVariant[] {
    return (EXAM_VARIANTS as Record<string, readonly ExamVariant[]>)[programme ?? ''] ?? [];
}

// Every programme a student can be registered into: base plus international.
// What admin surfaces offer — they are never gated.
export const REGISTRABLE_PROGRAMME_OPTIONS = [...PROGRAMME_OPTIONS, ...INTERNATIONAL_PROGRAMME_OPTIONS] as const;

// EXTENDED — programmes that exist so curricula can name them, offered only
// where explicitly enabled (a feature gate, or an admin authoring surface).
// Nobody registers as "AP": a US student is enrolled in AP Calculus AB, which
// is a curriculum, not a programme.
export const EXTENDED_PROGRAMME_OPTIONS = ['AP', 'COMMON_CORE', 'US_STATE_DISTRICT'] as const;
export type ExtendedProgrammeType = (typeof EXTENDED_PROGRAMME_OPTIONS)[number];

// FULL — the whole vocabulary: what a `Curriculum.board` and a pool key may
// name. Use this ONLY where a US curriculum must be expressible; everything
// student-facing stays on the base list, plus the international tier where its
// gate is on.
export const ALL_PROGRAMME_OPTIONS = [...PROGRAMME_OPTIONS, ...INTERNATIONAL_PROGRAMME_OPTIONS, ...EXTENDED_PROGRAMME_OPTIONS] as const;
export type AllProgrammeType = (typeof ALL_PROGRAMME_OPTIONS)[number];

// `ProgrammeType` is the FULL union, because it is the type of a programme value
// that has been STORED or PARSED — a `Curriculum.board`, a pool key's board half
// — and those may name an extended programme. The base/extended split is
// enforced by which ARRAY a surface maps over, never by the type: a picker that
// should not offer AP maps PROGRAMME_OPTIONS, and no type can tell it to.
// Reach for `BaseProgrammeType` only where a value is genuinely constrained to
// the base five; a narrow type on a stored field makes an AP row unwritable
// while leaving the picker free to offer it.
export type ProgrammeType = AllProgrammeType;

/** True when `b` is an international programme — registrable, but gated. */
export function isInternationalProgramme(b: string | null | undefined): boolean {
    return !!b && (INTERNATIONAL_PROGRAMME_OPTIONS as readonly string[]).includes(b.toUpperCase());
}

/**
 * The classes an exam's syllabus spans, for the exam tracks whose syllabus is
 * fixed whatever class the student is in. A class-9 student practising a JEE
 * rotation paper is sitting class-11 material; filing it against the class-9
 * topic list put every question under the nearest wrong chapter. School boards
 * (CBSE, ICSE, STATE) are absent: their syllabus IS the class.
 */
export const EXAM_SYLLABUS_GRADES: Readonly<Partial<Record<string, readonly GradeType[]>>> = {
    JEE: ['11', '12'],
    NEET: ['11', '12'],
};

/** The classes to classify a paper for this exam against, merged with the
 *  classes it was otherwise scoped to: an exam's own syllabus WIDENS the list
 *  (a class-9 JEE foundation paper still finds its class-9 topics). Unchanged
 *  for a school board, or when there was no class scope at all. */
export function widenGradesForExam(
    grades: readonly string[] | undefined,
    exam: string | undefined,
): readonly string[] | undefined {
    const syllabus = exam ? EXAM_SYLLABUS_GRADES[exam.toUpperCase()] : undefined;
    if (!syllabus || !grades?.length) return grades;
    return [...new Set([...grades, ...syllabus])];
}

/** True when `b` is an extended programme — one offered only where enabled. */
export function isExtendedProgramme(b: string | null | undefined): boolean {
    return !!b && (EXTENDED_PROGRAMME_OPTIONS as readonly string[]).includes(b.toUpperCase());
}

// ── Grades a programme covers ───────────────────────────────────────────────
// A programme absent from this table covers every grade in GRADE_OPTIONS. One
// listed here covers only these, and the rule holds everywhere a (programme,
// grade) pair is written: a profile, a curriculum, a pool key. `ib:7` is not a
// pool that can exist.
export const PROGRAMME_GRADES: Partial<Record<AllProgrammeType, readonly GradeType[]>> = {
    IB: ['8', '9', '10', '11', '12'],
    CAMBRIDGE: ['8', '9', '10', '11', '12'],
};

// ── The qualification a (programme, grade) sits in ──────────────────────────
// Display only. A stage is what a student and teacher call the class — "AS
// Level", "MYP" — and is never an identity: pools, curricula and profiles key
// on (programme, grade). Two courses at one grade (IB Maths AA vs AI, IGCSE vs
// O Level) are told apart by a minted pool key, not by a stage.
export const PROGRAMME_STAGES: Partial<Record<AllProgrammeType, Partial<Record<GradeType, string>>>> = {
    IB: { '8': 'MYP', '9': 'MYP', '10': 'MYP', '11': 'Diploma', '12': 'Diploma' },
    CAMBRIDGE: { '8': 'IGCSE', '9': 'IGCSE', '10': 'IGCSE', '11': 'AS Level', '12': 'A Level' },
};

/** 'AS Level' for (CAMBRIDGE, 11); null for a programme without stages or a
 *  grade it does not teach. */
export function programmeStage(programme: string | null | undefined, grade: string | number | null | undefined): string | null {
    if (grade == null) return null;
    return PROGRAMME_STAGES[(programme ?? '').toUpperCase() as AllProgrammeType]?.[String(grade) as GradeType] ?? null;
}

/** The grades `programme` covers, in order. Every grade for an unlisted or
 *  unknown programme — the table narrows, it never invents a restriction. */
export function gradesForProgramme(programme: string | null | undefined): readonly GradeType[] {
    return PROGRAMME_GRADES[(programme ?? '').toUpperCase() as AllProgrammeType] ?? GRADE_OPTIONS;
}

/** True when `grade` is one `programme` covers. Case-insensitive on the programme. */
export function isGradeValidForProgramme(programme: string | null | undefined, grade: string | number | null | undefined): boolean {
    if (grade == null) return false;
    return (gradesForProgramme(programme) as readonly string[]).includes(String(grade));
}

/** The programmes in `programmes` that do NOT cover `grade` — [] when every one does. */
export function programmesNotCoveringGrade(programmes: readonly string[] | null | undefined, grade: string | number | null | undefined): string[] {
    if (grade == null) return [];
    return (programmes ?? []).filter(p => !isGradeValidForProgramme(p, grade));
}

export const SUBJECT_OPTIONS = ['maths', 'physics', 'chemistry', 'biology'] as const;
export type SubjectType = (typeof SUBJECT_OPTIONS)[number];

export const SUBJECT_NAMES: Record<string, string> = {
    maths: 'Maths',
    physics: 'Physics',
    chemistry: 'Chemistry',
    biology: 'Biology',
};

// ── Extended subjects ───────────────────────────────────────────────────────
// Subjects beyond the core four, available ONLY on the curriculum-definition and
// revision-plan surfaces and gated by the `extended_subjects` feature gate. They
// are NOT core subjects: never add them to SUBJECT_OPTIONS, and they carry no
// question bank / practice / scoring — except those listed in
// QUESTION_EXTENDED_SUBJECT_OPTIONS below. See docs/FeatureDesigns/extended-subjects.md.
//
// SINGLE SOURCE OF TRUTH — to add a new extended subject, add one string here.
// The Zod validator, admin curriculum pickers, the student profile picker and the
// Revision tab all derive from this list; display name and colour derive too.
export const EXTENDED_SUBJECT_OPTIONS = ['history', 'geography', 'economics', 'hindi', 'english_literature', 'english_grammar', 'sanskrit'] as const;
export type ExtendedSubjectType = (typeof EXTENDED_SUBJECT_OPTIONS)[number];

// The union used ONLY by curriculum-content + revision models/endpoints/pickers.
export const ALL_SUBJECT_OPTIONS = [...SUBJECT_OPTIONS, ...EXTENDED_SUBJECT_OPTIONS] as const;
export type ContentSubjectType = (typeof ALL_SUBJECT_OPTIONS)[number];

// ── Question subjects ───────────────────────────────────────────────────────
// The one exception to "extended subjects carry no question bank": extended
// subjects that DO have questions, practised only through Chapter Practice links
// (not the Practice tab, Home or the subject grid). Today that is ICSE Class 6
// Geography alone. A subject here is metered like a core one — it is a
// subscription subject with its own analysis-quota bucket — and is offered in the
// admin question pickers. See docs/FeatureDesigns/geography-questions-target.md.
export const QUESTION_EXTENDED_SUBJECT_OPTIONS = ['geography'] as const;

// Every subject that has a question bank: the core four plus the exception above.
// The subscription/eligibility subject list (`VALID_SUBJECTS`) and the admin
// question pickers derive from this; `SUBJECT_OPTIONS` stays the core four for
// everything a student sees.
export const QUESTION_SUBJECT_OPTIONS = [...SUBJECT_OPTIONS, ...QUESTION_EXTENDED_SUBJECT_OPTIONS] as const;
export type QuestionSubjectType = (typeof QUESTION_SUBJECT_OPTIONS)[number];

/** True when `s` is one of the extended (non-core) subjects. Case-insensitive. */
export function isExtendedSubject(s: string): boolean {
    return (EXTENDED_SUBJECT_OPTIONS as readonly string[]).includes(s.toLowerCase());
}

// Display label: title-case the key by default; override only labels the key
// cannot produce (a future 'ncert-maths' → 'NCERT Maths'). The fallback treats
// `_` and `-` as word breaks, so a multi-word key no longer NEEDS an entry —
// these two are kept because they are the documented pair and removing them
// would make the override table look unused.
const EXTENDED_SUBJECT_LABEL_OVERRIDES: Partial<Record<ExtendedSubjectType, string>> = {
    english_literature: 'English Literature',
    english_grammar: 'English Grammar',
};

/** Human label for an extended subject (falls back to capitalising the key). */
export function extendedSubjectLabel(name: string): string {
    const key = name.toLowerCase();
    return EXTENDED_SUBJECT_LABEL_OVERRIDES[key as ExtendedSubjectType]
        ?? titleCaseSubjectKey(key);
}

/** `english_grammar` → "English Grammar". The fallback every subject label
 *  lands on when nothing better is known — a key's separators are word breaks,
 *  and leaving them in announces the key as a key. */
export function titleCaseSubjectKey(key: string): string {
    return key.split(/[_-]+/).filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

// Colour: deterministic from a small palette by list position — no per-subject
// entry, so a new extended subject gets a distinct colour for free.
const EXTENDED_SUBJECT_PALETTE = ['#a855f7', '#0ea5e9', '#f43f5e', '#14b8a6', '#f97316', '#84cc16'];

/** Deterministic accent colour for an extended subject. */
export function extendedSubjectColor(name: string): string {
    const i = (EXTENDED_SUBJECT_OPTIONS as readonly string[]).indexOf(name.toLowerCase());
    return EXTENDED_SUBJECT_PALETTE[(i < 0 ? 0 : i) % EXTENDED_SUBJECT_PALETTE.length];
}

// Numeric grade bounds used by Zod number().min/max validators.
export const GRADE_MIN_NUMERIC = Number(GRADE_OPTIONS[0]);
export const GRADE_MAX_NUMERIC = Number(GRADE_OPTIONS[GRADE_OPTIONS.length - 1]);
